import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { MdContentCopy } from 'react-icons/md';
import * as yup from 'yup';
import { useFormik } from 'formik';
import {
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
} from '@mui/material';

import {
  MSG_REQUIRED_FIELD,
  LINK_VARIABLES,
  SMS_LINK_TRIGGER,
} from '../../utils/contants';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Patch, Post } from '../../Axios/AxiosFunctions';
import { Button } from '../../Component/Button/Button';
import { Checkbox } from '../../Component/Checkbox/Checkbox';
import SideBarSkeleton from '../../Component/SideBarSkeleton';
import classes from './AddAutomatedSms.module.css';

const validationSchema = yup.object().shape({
  trigger: yup.string().required(MSG_REQUIRED_FIELD),
  to: yup.string().required(MSG_REQUIRED_FIELD),
  message: yup.string().required(MSG_REQUIRED_FIELD),
});

const AddAutomatedSms = ({ title, editData }) => {
  const navigate = useNavigate();

  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer,
  );
  const baseApiUrl = BaseURL(`automated-sms-template`);

  const [variableSearch, setVariableSearch] = useState('');
  const filteredVariables = useMemo(() => {
    const term = variableSearch.trim().toLowerCase();
    if (!term) return LINK_VARIABLES;
    return LINK_VARIABLES.filter(
      (v) =>
        v.label.toLowerCase().includes(term) ||
        v.value.toLowerCase().includes(term),
    );
  }, [variableSearch]);

  const {
    errors,
    values,
    handleChange,
    touched,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setSubmitting,
    resetForm,
    handleBlur,
  } = useFormik({
    initialValues: {
      trigger: editData?.trigger || '',
      to: editData?.to || '',
      cc: editData?.cc || '',
      message: editData?.message || '',
      isEnabled: editData?.isEnabled ?? true,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (formData) => {
      setSubmitting(true);

      if (editData?._id) {
        const response = await Patch(
          `${baseApiUrl}/${editData?._id}`,
          formData,
          apiHeader(accessToken),
        );

        if (response !== undefined) {
          toast.success(`SMS Edited Successfully.`);
          setSubmitting(false);
          navigate('/automated-emails?tab=sms');
        } else {
          setSubmitting(false);
        }
      } else {
        const response = await Post(
          baseApiUrl,
          formData,
          apiHeader(accessToken),
          companyId,
        );

        if (response !== undefined) {
          toast.success(`SMS Created Successfully.`);
          setSubmitting(false);
          navigate('/automated-emails?tab=sms');
          resetForm();
        } else {
          setSubmitting(false);
        }
      }
    },
  });

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Variable Copied');
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <SideBarSkeleton>
      <div className={classes.container_main}>
        <div className={classes.main_heading}>
          <h4>
            {title
              ? title
              : `${editData?._id ? 'Edit' : 'Add'} Automated SMS`}
          </h4>
        </div>

        <form
          onSubmit={handleSubmit}
          className={classes.addSmsTemplateModal_main}
        >
          <Row className={classes.input_row}>
            <FormControl
              fullWidth
              error={touched.trigger && Boolean(errors.trigger)}
            >
              <InputLabel id="trigger-label">Trigger *</InputLabel>
              <Select
                labelId="trigger-label"
                name="trigger"
                value={values.trigger}
                onChange={handleChange}
                onBlur={handleBlur}
                label="Trigger *"
                autoFocus
              >
                {SMS_LINK_TRIGGER.map((trigger) => (
                  <MenuItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </MenuItem>
                ))}
              </Select>
              {touched.trigger && errors.trigger && (
                <FormHelperText>{errors.trigger}</FormHelperText>
              )}
            </FormControl>

            <TextField
              label="To phone *"
              name="to"
              onChange={handleChange}
              value={values.to}
              error={touched.to && Boolean(errors.to)}
              helperText={touched.to && errors.to}
            />

            <TextField
              label="CC (additional recipients)"
              name="cc"
              placeholder="e.g. broker phone or *|broker-phone|*"
              onChange={handleChange}
              value={values.cc}
              error={touched.cc && Boolean(errors.cc)}
              helperText={touched.cc && errors.cc}
            />

            <TextField
              className={classes.full_row}
              label="Message *"
              name="message"
              multiline
              minRows={6}
              onChange={handleChange}
              value={values.message}
              error={touched.message && Boolean(errors.message)}
              helperText={touched.message && errors.message}
            />

            <div className={`${classes.var_section} ${classes.full_row}`}>
              <TextField
                size="small"
                placeholder="Search variables"
                value={variableSearch}
                onChange={(e) => setVariableSearch(e.target.value)}
                fullWidth
              />
              {filteredVariables.length > 0 ? (
                filteredVariables.map((variable) => (
                  <div className={classes.var_item} key={variable.id}>
                    {variable.label} ({variable.value})
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => copyText(variable.value)}
                    >
                      <MdContentCopy />
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ color: '#888', fontSize: 14 }}>
                  No variables match your search.
                </div>
              )}
            </div>

            <Checkbox
              setValue={(e) => setFieldValue('isEnabled', e === 'Active')}
              value={values.isEnabled ? 'Active' : ''}
              label={'Active'}
            />
          </Row>

          <div className={classes.btn_main}>
            <Button
              type="submit"
              className={classes.btn}
              label={
                isSubmitting
                  ? 'Submitting...'
                  : editData?._id
                    ? 'Edit SMS'
                    : 'Add SMS'
              }
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </SideBarSkeleton>
  );
};

export default AddAutomatedSms;
